import { computed, ComputedRef } from 'vue'
import { Module, ModuleTree, MutationTree, ActionTree, GetterTree, ActionContext } from 'vuex'
import { RouteLocationNormalizedLoaded, RouteRecordName, RouteRecordRaw } from 'vue-router'

import store, { StateInterface } from '@/store'
import { View, ViewCollection, ViewInstanceOptions, ViewManagerOptions, ViewState } from '@/models/view'
import Size from '@/models/size'
import { EntryConfig } from '@Protium/vue-ozone-utils'

interface ViewManagerMethods {
  moduleName: string,
  subModule: ComputedRef<string>,
  views: ComputedRef<{id: string, name: string}[]>,
  resetView():void,
  fromRoute(route: RouteLocationNormalizedLoaded | RouteRecordRaw):void,
  addViews(views: ViewCollection):void,
  showView(view: View | View['id']):void,
  activeViewModule:ComputedRef,
  activeView: ComputedRef<string>,
  filters: Record<string, any>,
  activeViewName: ComputedRef<string>,
  viewMode(mode: string | number):void
}

const DEFAULT_ENTRY_CONFIG = {
  actions: {
    edit: true, // allows or disallows any of the below,
    create: true,
    add: false,
    remove: true,
    search: true,
    allow: {
      delete: true
    }
  },
  unique: true,
  replace: false,
  draft: false, // if a new entry should be created based on the available model, or if a database call should be made immediately
  fields: {
    sys: 'sys',
    data: '',
    title: ''
  },
  links: {
    base: '/'
  },
  labels: {
    add: 'Add new',
    search: 'Add Existing',
    remove: 'Remove'
  },
  debug: {
    sys: true,
    verbose: true
  }
}
const DEFAULT_ITEM_CONFIG = {
  actions: {
    remove: true
  },
  size: Size.medium,
  breakPoints: {
    smallMedium: 300
  }
}

function toViewsArray (viewsDict: ViewCollection) {
  return Object.entries(viewsDict).map(e => ({ id: e[0], name: e[1] }))
}

function ViewInstance<T extends ViewCollection> (
  id: string,
  views: T,
  {
    state = {} as ViewState<T>,
    getters = {},
    modules = {},
    mutations = {},
    actions = {}
  },
  opts: ViewInstanceOptions = { itemSize: Size.medium, defaultView: 'Overview' }
): Module<ViewState<T>, StateInterface> {
  const instance = {
    namespaced: true,
    state: {
      entryConfig: DEFAULT_ENTRY_CONFIG,
      itemConfig: { ...DEFAULT_ITEM_CONFIG, size: opts.itemSize || Size.medium },
      // itemSize: opts.itemSize || Size.medium,
      activeViewName: views ? views[opts.defaultView || 0] : undefined, // default
      viewMode: undefined,
      ...state,
      filters: state.filters || {
        // @Filters type. sets the specified "filters" enum pairs to plain object id => name pairs
        views: toViewsArray(views)
      },
    } as ViewState<T>,
    mutations: {
      SetEntryConfig (state: ViewState<T>, entryConfig: EntryConfig): void {
        state.entryConfig = entryConfig
      },
      SetItemConfig (state: ViewState<T>, itemConfig: EntryConfig): void {
        state.itemConfig = itemConfig
      },
      SetListActions (state: ViewState<T>, actions: Record<string, any> = {}): void {
        Object.keys(actions).forEach((action: string) => {
          if (state.entryConfig?.actions) {
            if (typeof actions[action] === 'boolean') state.entryConfig.actions[action] = actions[action] as boolean
          } else {
            if (!state.entryConfig) {
              state.entryConfig = DEFAULT_ITEM_CONFIG
              state.entryConfig.actions = { [action]: actions[action] }
            }
          }
        })
      },
      SetItemActions (state: ViewState<T>, actions: Record<string, any> = {}): void {
        Object.keys(actions).forEach((action: string) => {
          if (state.itemConfig?.actions) {
            if (typeof actions[action] === 'boolean') state.itemConfig.actions[action] = actions[action] as boolean
          } else {
            if (!state.itemConfig) {
              state.itemConfig = DEFAULT_ITEM_CONFIG
              state.itemConfig.actions = { [action]: actions[action] }
            }
          }
        })
      },
      SetItemSize (state: ViewState<T>, size: Size) {
        if (state.itemConfig) {
          state.itemConfig.size = size
        }
      },
      ShowView (state: ViewState<T>, viewId: T) {
        // COMBAK crude support for shared views
        state.activeViewName = viewId
      },
      Reset (state: ViewState<T>) {
        state.activeViewName = views[0]
      },
      ViewMode (state: ViewState<T>, mode: string | number) {
        state.viewMode = mode
      },
      AddView (state: ViewState<T>, view: View) {
        state.filters?.views.push(view)
      },
      ...mutations
    } as MutationTree<ViewState<T>>,
    actions: {
      SetListActions: ({ state, commit }, actions) => {
        commit('SetListActions', actions)
      },
      SetItemActions: ({ state, commit }, actions) => {
        commit('SetItemActions', actions)
      },
      ToggleItemSize: ({ state, commit }) => {
        if (state.itemConfig?.size === Size.medium) {
          commit('SetItemSize', Size.small)
        } else {
          commit('SetItemSize', Size.medium)
        }
      },
      ...actions
    } as ActionTree<ViewState<T>, StateInterface>,
    getters: {
      ActiveViewName (context) {
        return views[
          Object.keys(views).filter(
            f => f === ((context.activeViewName as unknown) as string)
          )[0]
        ]
      },
      ViewsArray (context) {
        return context.filters.views
      },
      Views (context) {
        return Object.fromEntries(context.filters.views.map(v => ([v.id, v.name])))
      },
      ActiveView (context) {
        return Object.keys(views).filter(
          f => f === ((context.activeViewName as unknown) as string)
        )[0]
      },
      Filters (context) {
        return context.filters
      },
      EntryConfig (context) {
        return context.entryConfig
      },
      ItemConfig (context) {
        return context.itemConfig
      },
      ItemSize (context) {
        if (context.itemConfig) {
          return context.itemConfig.size
        }
      },
      ...getters
    } as GetterTree<ViewState<T>, StateInterface>,
    modules
  }

  return instance as Module<ViewState<T>, StateInterface>
}

const VIEW_MODULE_ROOT = 'View'

export default class {
  public _modules: ModuleTree<StateInterface>
  private options: ViewManagerOptions

  constructor (options?: ViewManagerOptions) {
    this.options = options || {}
    this._modules = {}
  }

  get moduleRoot () {
    return VIEW_MODULE_ROOT
  }

  fromRoutes (routes: RouteRecordRaw[] | RouteLocationNormalizedLoaded[]): this {
    routes.forEach((route: RouteRecordRaw | RouteLocationNormalizedLoaded) => {
      // console.log('fromRoutes | checking...', route.name, route.meta)
      if (route.name && route.meta?.filters && route.meta?.isView) {
        const routeFilters:ViewCollection = route.meta?.filters as any || ViewCollection
        // console.log('fromRoutes | adding', route.name.toString(), routeFilters)
        this.add(route.name.toString(), routeFilters)
      }
      // console.log('fromRoutes', this._modules)

      if ((route as RouteRecordRaw).children) this.fromRoutes((route as RouteRecordRaw).children || []) // recurse
    })

    return this
  }

  // NOTE that this calls to the store, and not this.modules since it is static
  // in effect updating this.modules after using the default export from this module has no effect
  static compose (route: RouteRecordName | null | undefined, moduleName = VIEW_MODULE_ROOT): ViewManagerMethods {
    // console.log('compose', route)
    try {
      if (!moduleName) throw Error('[Viewmanager.compose] missing store module name')
      if (!route) throw Error('[Viewmanager.compose] route argument is null or undefined, please make sure the route is correct')

      const activeViewModule = computed(() => store.getters[`${moduleName}/ViewModule`](route))
      const subModule = computed(() => `${moduleName}/${activeViewModule.value}`)
      const views = computed(() => {
        const v: ViewCollection = store.getters[`${subModule.value}/Views`]
        if (!v) {
          if (activeViewModule.value !== undefined) console.warn(`No views found for submodule ${subModule.value}!`)
          return []
        }
        return Object.keys(v).map(k => ({ id: k, name: v[k] }))
      })

      const activeView = computed(() =>
        activeViewModule.value && store.getters[`${subModule.value}/ActiveView`]
          ? store.getters[`${subModule.value}/ActiveView`]
          : null
      )

      const activeViewName = computed(() =>
        activeViewModule.value
          ? store.getters[`${subModule.value}/ActiveViewName`]
          : null
      )

      const filters = computed(() =>
        activeViewModule.value
          ? store.getters[`${subModule.value}/Filters`]
          : []
      )

      const showView = (view: View | View['id']): void => {
        if (!view) {
          console.error('showView called with undefined view value!')
          return store.commit(`${subModule.value}/ShowView`, null)
        }
        store.commit(`${subModule.value}/ShowView`, typeof view === 'string' ? view : view.id)
      }

      const resetView = (): void => {
        store.commit(`${subModule.value}/Reset`)
      }

      const viewMode = (mode: string | number): void => {
        store.commit(`${subModule.value}/ViewMode`)
      }

      const fromRoute = (route): void => {
        if (route.name && route.meta?.filters && route.meta?.isView) {
          const routeFilters: ViewCollection = route.meta?.filters || ViewCollection
          addViews(routeFilters)
        }
      }

      const addViews = (views: ViewCollection): void => {
        store.commit(`${subModule.value}/AddView`, toViewsArray(views))
      }

      return {
        moduleName,
        views,
        fromRoute,
        addViews,
        resetView,
        activeViewModule,
        activeView,
        subModule,
        filters,
        activeViewName,
        showView,
        viewMode
      }
    } catch (e: any) {
      console.error(`Error while composing active view controller for ${String(route)}`, e)
      return e
    }
  }

  module (id: string): Module<unknown, StateInterface> {
    return this._modules[id]
  }

  get modules (): ModuleTree<StateInterface> {
    return this._modules
  }

  set modules (modules: ModuleTree<StateInterface>) {
    this._modules = modules
  }

  add<T extends ViewCollection> (routeName: string, views: T, opts?: ViewInstanceOptions): Module<ViewState<T>, StateInterface> {
    this._modules[routeName] = ViewInstance<T>(routeName, views, {}, opts)
    return this._modules[routeName]
  }

  // the instance for exporting from vuex module
  get instance (): Module<ViewState<ViewCollection>, StateInterface> {
    const getters = {
      ViewModule: () => (currentView: string) => {
        // console.log('[ViewManager.ViewModule] currentView - ', currentView)

        if (!this.modules[currentView]) {
          if (process.env.NODE_ENV !== 'production') console.warn(`[View] No namespaced module ${currentView} has been found. Make sure that route name equals module id`)
          // console.log('[ViewManager.ViewModule] available modules', this.modules)

          return
        }
        return currentView
      }
    }

    const actions = {
      Register: (context: ActionContext<ViewState<any>, StateInterface>, { routeName, views, opts }: { routeName: string, views: ViewCollection, opts?:ViewInstanceOptions}) => {
        // console.log('Register View Module', routeName, views)
        store.registerModule(`${VIEW_MODULE_ROOT}/${routeName}`, this.add(routeName, views, opts))
      }
    }

    return ViewInstance<ViewCollection>(
      'root',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      {},
      { getters, actions, modules: this.modules }
    )
  }
}
